import { Injectable } from "@angular/core";
import { INewsPost } from "@api/models/news/news";
import { AppTeam } from "@shared/models/team";
import { ImageSubType } from "@shared/models/image/image-sub-type";
import { ImageService } from "@utils/services/image/image.service";
import { IMAGE_RESOLUTION } from "@api/models/image/image-resolution";
import { TypeService } from "@core/services/type.service";
import { PageType } from "../../../../../page-type";
import { LinkService } from "@core/services/link.service";
import { HomeRoutes } from "@app/routes/home.routes";
import { AppLeague } from "@shared/models/league";
import { SharingService } from "@core/services/sharing.service";
import { TeamService } from "@core/services/team.service";
import { LeagueService } from "@core/services/league.service";
import { SEOService } from "@core/services/seo.service";
import {
  NewsHttpService,
  NewsTargetType,
} from "@api/http/news/news-http.service";
import { StateService } from "@core/services/state.service";
import { UrlService } from "@utils/services/url/url.service";
import {
  IndexedLeague,
  LeagueHttpService,
} from "@api/http/league/league-http.service";
import { TaboolaCategories } from "@shared/models/taboola/taboola-categories";
import { StreamingHttpService } from "@api/http/streaming/streaming-http.service";
import { IShopItem } from "@api/models/shop/shop-item";
import { KroneHttpService } from "@api/http/krone/krone-http.service";

@Injectable()
export class NewsDetailService {

  public shopItems: IShopItem[] = [];
  public newsEntry: INewsPost;
  public topNews: INewsPost[];
  public leagueNews: INewsPost[];
  public videos: INewsPost[];
  public league: AppLeague;
  public leagueLink: string;
  public loading = true;
  public hasImages = false;
  public hasVideos = false;
  public teamId: string;
  public votingId: string;
  public topNewsLoading = false;
  public leagueNewsLoading = false;

  constructor(
    private sharingService: SharingService,
    private teamService: TeamService,
    private leagueService: LeagueService,
    private seoService: SEOService,
    private newsHttpService: NewsHttpService,
    private kroneHttp: KroneHttpService,
    private leagueHttpService: LeagueHttpService,
    private streamingHttp: StreamingHttpService,
  ) {}

  public async init(news: INewsPost): Promise<void> {
    this.loading = true;
    this.newsEntry = news;
    this.votingId = this.newsEntry.info?.votingid;
    this.hasImages = this.newsEntry.pictures?.length > 0;
    this.hasVideos = this.newsEntry.videos_native?.length > 0;
    this.setMetadata();
    this.setCanonicalTags(this.newsEntry);
    try {
      if (TypeService.pageType === PageType.Home) {
        await this.setLeagueForCreator(this.newsEntry);
      }
      if (TypeService.pageType === PageType.Team) {
        await this.setLeagueForTeam(this.teamService.currentTeam);
      }
      if (TypeService.pageType === PageType.League) {
        this.league = this.leagueService.currentLeague;
      }
      if (!this.league) {
        const league = (await this.leagueHttpService.getNamedLeague(
          IndexedLeague.Bundesliga
        )) as any;
        this.league = new AppLeague(league);
      }
      this.leagueLink = `https://${this.league?.readable_id}.${UrlService.WebHost}`;
      if (StateService.isBrowser) {
        this.getVideos();
      }
      this.getShopItems();
      this.loading = false;
    } catch (e) {
      this.loading = false;
      throw e;
    }
  }

  public async getTopNews(): Promise<void> {
    try {
      this.topNewsLoading = !this.topNews;
      this.topNews = await this.newsHttpService.getTopNews();
      this.topNewsLoading = false;
    } catch (e) {
      this.topNewsLoading = false;
      throw e;
    }
  }

  public async getLeagueNews(): Promise<void> {
    if (!this.league) {
      return;
    }
    try {
      this.leagueNewsLoading = !this.leagueNews;
      const response = await this.newsHttpService.getNewsForTargetPaginated(
        NewsTargetType.League,
        this.league.id,
        0,
        8,
        false,
        true,
        true
      );
      this.leagueNews = response.data;
      this.leagueNewsLoading = false;
    } catch (e) {
      this.leagueNewsLoading = false;
      throw e;
    }
  }

  private async getShopItems() {
    const response = await this.kroneHttp.getShopItems();
    if (response) {
      this.shopItems = response.data
    }
  }

  private async getVideos(): Promise<void> {
    const response = await this.streamingHttp.getVideoNews(0, 8, { leagueId: this.league?._id });
    this.videos = response.data;
  }

  private async setLeagueForCreator(entry: INewsPost): Promise<void> {
    if (entry.creator.type === "team") {
      if (entry.creator.creatorid) {
        this.teamId = entry.creator.creatorid;
        await this.setLeagueForTeamId(this.teamId);
      }
    }
    if (entry.creator.type === "newsprofile") {
      if (entry.ui_tags.league_tags[0]?._id) {
        const leagueId = entry.ui_tags.league_tags[0]._id;
        await this.getLeague(leagueId);
      } else if (entry.ui_tags.team_tags[0]?._id) {
        this.teamId = entry.ui_tags.team_tags[0]?._id;
        await this.setLeagueForTeamId(this.teamId);
      }
    }
  }

  private async setLeagueForTeam(team: AppTeam): Promise<void> {
    const leagueIdForTable = team.getLeagueForTable();
    this.teamId = team.id;
    if (leagueIdForTable) {
      this.league = await this.leagueService.getLeague(leagueIdForTable);
    }
  }

  private async setLeagueForTeamId(teamId: string): Promise<void> {
    const team = await this.teamService.getTeam(teamId);
    await this.setLeagueForTeam(team);
  }

  private async getLeague(leagueId: string): Promise<void> {
    this.league = await this.leagueService.getLeague(leagueId);
  }

  private async setMetadata(): Promise<void> {
    const sharingData = await this.sharingService.fetchNewsEntrySharingInfo(
      this.newsEntry._id
    );
    // If news entry without images, use creator logo
    let imageUrl = sharingData.image_url;
    if (!imageUrl?.length) {
      imageUrl = ImageService.StaticImages.Seo.defaultImage;
    }
    if (!this.hasImages) {
      const creatorLogo = this.newsEntry.creator.pictures.find(
        (pic) => pic.subtype === ImageSubType.LOGO
      );
      if (creatorLogo) {
        imageUrl = ImageService.getPictureUrl(
          creatorLogo,
          IMAGE_RESOLUTION.HIGH
        );
      }
    }

    // News entry with video preview
    if (this.hasVideos && this.newsEntry.videos_native[0].preview) {
      imageUrl = ImageService.getPictureUrl(
        this.newsEntry.videos_native[0].preview,
        IMAGE_RESOLUTION.MEDIUM
      );
    }

    let title = "";
    title += sharingData.title?.length > 0 ? sharingData.title + " | " : "";
    if (this.newsEntry.creator.name === "fan.at") {
      title += "fan.at";
    } else {
      title += this.newsEntry.creator.name + " | fan.at";
    }

    this.seoService.setTitle(title);
    if (sharingData.description) {
      this.seoService.setDescription(sharingData.description);
    }
    this.seoService.setImage(imageUrl);
    if (sharingData.content_url) {
      this.seoService.setAppLinks(sharingData.content_url);
    }

    // Taboola settings
    if (
      sharingData?.title?.length > 0 &&
      !sharingData.title.includes("Fotos von") &&
      !sharingData.title.includes("Photos from")
    ) {
      this.seoService.setTaboolaTitle(sharingData.title);
    }
    if (this.newsEntry.sponsored) {
      this.seoService.setTaboolaCategory(TaboolaCategories.Advertorial);
    }
    // Voting tags are set in the voting controller component
  }

  private setCanonicalTags(entry: INewsPost): void {
    if (entry.external_link) {
      this.seoService.updateCanonicalUrl(entry.external_link);
      return;
    }
    if (TypeService.pageType !== PageType.Home) {
      const link = `${LinkService.getHomeLink()}/${HomeRoutes.news.root}/${
        entry._id
      }`;
      this.seoService.updateCanonicalUrl(link);
    }
  }
}
